import { useEffect, useRef, useState } from "react";
import { Check, CheckCircle2, RefreshCw, Send, Sparkles } from "lucide-react";
import { useSimplAI } from "../../../imports/useSimpleAI";
import { createPrRequest, uploadIacAgentBackendData, resolveSupplier } from "../../../services/IacEligibilityService";
import { PrDetailsPanel, PrRecord, AgentEvaluation } from "./PrDetailsPanel";
import { getAuthFromCookie } from "../auth/auth";
import {
  businessUnitForSupplier,
  contractFor,
  findCatalogEntry,
  findSupplierName,
  PrMasterRow,
  SUPPLIER_NAMES,
  supplierIdFor,
} from "./prCatalog";

type Role = "user" | "agent";

interface ChatMessage {
  id: number;
  role: Role;
  text: string;
}

interface PrDraft {
  supplier?: string;
  supplierCode?: string;
  supplierDbId?: number;
  description?: string;
  category?: string;
  amount?: number | null;
  currency?: string;
  isRecurring?: boolean;
  catalog?: PrMasterRow;
}

type Stage = "collecting" | "verifying" | "verified";

// Each starter prompt is paired with a follow-up answer drawn from the same contract/line-item
// so a second click can supply every detail the intro question asks for, with no typing required.
const SUGGESTIONS: { prompt: string; followUp: string }[] = [
  {
    prompt: "Create a PR for HVAC preventive maintenance - Sep 2026",
    followUp:
      "Apex Facilities, Facilities, ₹450,000, recurring monthly — HVAC preventive maintenance for Jaipur site, needed by Sep 2026",
  },
  {
    prompt: "Create a PR for Annual analytics SaaS renewal",
    followUp:
      "Nimbus Software, Software, $72,000, recurring annually — Annual analytics platform license renewal for India",
  },
  {
    prompt: "Create a PR for Manned guarding services - Sep 2026",
    followUp:
      "Vertex Security, Security Services, ₹384,000, recurring monthly — Manned guarding services for Jaipur site, needed by Sep 2026",
  },
];

const buildIntroReply = (name: string) => [
  `Hi ${name}! I'd be happy to help you create a PR. I'll need a few details:`,
  "",
  "• Supplier name",
  "• Category",
  "• Estimated amount",
  "• Is this recurring?",
  "• Brief description",
  "",
  "Please share these details and I'll draft the PR for you.",
].join("\n");

const RECURRING_WORDS = /\b(recurring|quarterly|monthly|annual(?:ly)?|yearly|weekly|subscription|renewal|preventive)\b/i;
const ONE_OFF_WORDS = /\b(one[-\s]?off|one[-\s]?time|ad[-\s]?hoc|non[-\s]?recurring|emergency|single)\b/i;

const parseAmount = (text: string) => {
  const currency = text.match(/([$₹])\s?([\d,]+(?:\.\d+)?)\s*(k|m)?/i);
  if (currency) {
    const base = Number(currency[2].replace(/,/g, ""));
    const unit = currency[3]?.toLowerCase();
    const value = unit === "k" ? base * 1000 : unit === "m" ? base * 1000000 : base;
    return { amount: value, currency: currency[1] === "$" ? "USD" : "INR" };
  }

  const bare = text.match(/\b(\d{1,3}(?:,\d{3})+|\d{4,})\b/);
  return bare ? { amount: Number(bare[1].replace(/,/g, "")), currency: undefined } : undefined;
};
const extractSupplierCode = (text: string) => {
  return text.match(/\bSUP\d+\b/i)?.[0]?.toUpperCase();
};
const parseRecurring = (text: string) => {
  if (ONE_OFF_WORDS.test(text)) return false;
  if (RECURRING_WORDS.test(text)) return true;
  if (/^\s*(yes|yep|yeah)\b/i.test(text)) return true;
  if (/^\s*(no|nope)\b/i.test(text)) return false;
  return undefined;
};

// Description is resolved from the hardcoded PR master; the supplier narrows it by business unit.
const extractDraft = (text: string, current: PrDraft): PrDraft => {
  const next: PrDraft = { ...current };

  const supplier = findSupplierName(text);
  if (supplier) next.supplier = supplier;

  const catalog = findCatalogEntry(text, businessUnitForSupplier(next.supplier || ""));
  if (catalog) {
    next.catalog = catalog;
    // Only fill fields the user hasn't already provided; amount must always come from the user, never the catalog.
    next.description = next.description || catalog.description;
    next.category = next.category || catalog.category;
    next.currency = next.currency || catalog.currency;
  }

  const money = parseAmount(text);
  if (money) {
    next.amount = money.amount;
    if (money.currency) next.currency = money.currency;
  }

  const recurring = parseRecurring(text);
  if (recurring !== undefined) next.isRecurring = recurring;

  return next;
};

const evaluateEligibility = (draft: PrDraft) => {
  const supplierId = draft.supplier ? supplierIdFor(draft.supplier) : undefined;
  const contract = contractFor(supplierId, draft.catalog?.businessUnit);

  if (!contract) {
    return { supplierId, contract, isEligible: false, reason: "No contract found for this supplier in the contract master." };
  }
  if (contract.status !== "Active") {
    return { supplierId, contract, isEligible: false, reason: `Contract ${contract.contractId} is ${contract.status.toLowerCase()}.` };
  }
  if (!contract.iacEnabled) {
    return { supplierId, contract, isEligible: false, reason: `Contract ${contract.contractId} is not IAC enabled.` };
  }
  if (draft.amount != null && draft.amount > contract.remainingValue) {
    return { supplierId, contract, isEligible: false, reason: `Requested value exceeds the remaining contract value on ${contract.contractId}.` };
  }

  return { supplierId, contract, isEligible: true, reason: `Contract ${contract.contractId} is active, IAC enabled and has sufficient remaining value.` };
};

const findAgentPayload = (value: unknown): Record<string, unknown> | null => {
  if (typeof value === "string") {
    try {
      return findAgentPayload(JSON.parse(value));
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== "object") return null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findAgentPayload(item);
      if (found) return found;
    }
    return null;
  }

  const object = value as Record<string, unknown>;
  const agent = object["IAC Agent"];
  if (Array.isArray(agent) && agent[0] && typeof agent[0] === "object") {
    return agent[0] as Record<string, unknown>;
  }

  for (const child of Object.values(object)) {
    const found = findAgentPayload(child);
    if (found) return found;
  }
  return null;
};

const parseAgentEvaluation = (value: unknown, cId?: string, mId?: string): AgentEvaluation => {
  const result = findAgentPayload(value);
  if (!result) throw new Error("SimplAI response did not contain an IAC Agent result");

  return {
    classification: String(result.Classification || "Unknown"),
    matchedContract: String(result["Matched Contract"] || "N/A"),
    recommendationStrength: String(result["Recommendation Strength"] || "Unknown"),
    details: String(result.Details || "No additional details were returned."),
    cId,
    mId,
  };
};

export function CreatePrTab() {
  const userName = getAuthFromCookie()?.firstname || "there";
  const userInitials = (() => {
    const user = getAuthFromCookie();
    const initials = `${user?.firstname?.[0] || ""}${user?.lastname?.[0] || ""}`.toUpperCase();
    return initials || "U";
  })();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draftDetails, setDraftDetails] = useState<PrDraft>({});
  const [stage, setStage] = useState<Stage>("collecting");
  const [steps, setSteps] = useState<string[]>([]);
  const [record, setRecord] = useState<PrRecord | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isUploadingBackendData, setIsUploadingBackendData] = useState(false);
  const [draft, setDraft] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [linkedFollowUp, setLinkedFollowUp] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { runSimplAI } = useSimplAI();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, isTyping, steps, record]);

  const runVerification = async (details: PrDraft) => {
    setStage("verifying");
    setSaveError(null);

    const verdict = evaluateEligibility(details);
    const supplier = details.supplier || "";

    setSteps([`Supplier compliance verified (${supplier})`]);
    await new Promise((resolve) => window.setTimeout(resolve, 800));
    setSteps((prev) => [...prev, verdict.isEligible ? "IRC criteria matched — eligible" : "IRC criteria not met — review required"]);
    await new Promise((resolve) => window.setTimeout(resolve, 800));

    // Always mint a fresh id so repeat submissions never collide, regardless of catalog match.
    const uniquePrId = `PR-${Math.floor(100000 + Math.random() * 900000)}`;

    let savedId: number | undefined;
    try {
      const saved = await createPrRequest({
        supplier,
        category: details.category || details.catalog?.category || "Uncategorised",
        estimatedAmount: details.amount ?? null,
        isRecurring: Boolean(details.isRecurring),
        briefDescription: details.description || "",
        prId: uniquePrId,
        status: verdict.isEligible ? "IAC Eligible" : "Awaiting Evaluation",
      });
      savedId = saved.id;
      setSteps((prev) => [...prev, "Request saved to the PR register"]);

    } catch {
      setSaveError("Eligibility verified, but the PR could not be saved. Please retry.");
    }

    setRecord({
      eligibilityId: `IRC-${Math.floor(7000 + Math.random() * 999)}`,
      prId: uniquePrId,
      supplier,
      supplierId: details.supplierCode || verdict.supplierId,
      category: details.category || details.catalog?.category || "Uncategorised",
      amount: details.amount ?? null,
      currency: details.currency || details.catalog?.currency || "INR",
      description: details.description || "",
      isRecurring: Boolean(details.isRecurring),
      isEligible: verdict.isEligible,
      reason: verdict.reason,
      catalog: details.catalog,
      contract: verdict.contract,
      savedId,
    });
    setStage("verified");
  };

  const reset = () => {
    setMessages([]);
    setDraftDetails({});
    setSteps([]);
    setRecord(null);
    setSaveError(null);
    setStage("collecting");
    setShowDetails(false);
    setLinkedFollowUp(null);
  };

  const openDetails = async () => {
    setIsUploadingBackendData(true);
    setSaveError(null);

    try {
      const backendDataUrl = await uploadIacAgentBackendData();
      setRecord((current) => current ? { ...current, backendDataUrl } : current);
      setShowDetails(true);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Unable to upload IAC backend data.");
    } finally {
      setIsUploadingBackendData(false);
    }
  };

  const runAgentEvaluation = async () => {
    if (!record?.backendDataUrl) {
      throw new Error("IAC backend data is unavailable. Return to the PR and open View Details again.");
    }

    let conversation: { cId?: string; mId?: string } = {};
    let evaluation: AgentEvaluation | undefined;

    try {
      const response = await runSimplAI(
        {
          data: {
            input_data: {
              IAC_Agent_Sample_Backend_Data: record.backendDataUrl,
              Supplier_ID: "",
              Supplier: record.supplier,
              Description: record.description,
            },
          },
        },
        56,
        {
          onPostResponse: ({ cId, mId }) => {
            conversation = { cId, mId };
          },
          onPollComplete: ({ cId, mId, data }) => {
            evaluation = parseAgentEvaluation(data, cId, mId);
          },
        },
      );

      if (!evaluation) {
        evaluation = parseAgentEvaluation(response?.data, conversation.cId, conversation.mId);
      }
    } catch (error) {
      console.error("IAC agent evaluation failed:", error);
      throw error instanceof Error ? error : new Error("Unable to complete agent evaluation.");
    }

    setRecord((current) => current ? { ...current, agentEvaluation: evaluation } : current);
  };

  const submit = async (text: string, followUp?: string) => {
    const request = text.trim();
    if (!request || isTyping || stage !== "collecting") return;

    setMessages((prev) => [...prev, { id: prev.length + 1, role: "user", text: request }]);
    setDraft("");
    setIsTyping(true);
    setLinkedFollowUp(null);

   let merged = extractDraft(request, draftDetails);

const supplierCodeFromText = extractSupplierCode(request);

const supplierLookupValue =
  supplierCodeFromText ||
  merged.supplier ||
  request;

  const supplierCodeMatch = request.match(/\bSUP\d+\b/i);
let resolvedSupplier = null;

try {


if (supplierCodeMatch) {
  resolvedSupplier = await resolveSupplier(supplierCodeMatch[0]);
} else if (merged.supplier) {
  resolvedSupplier = await resolveSupplier(merged.supplier);
}
} catch (error) {
  console.error("SUPPLIER RESOLUTION ERROR:", error);
}


if (resolvedSupplier) {
  merged = {
    ...merged,

    supplier: resolvedSupplier.supplier,

    supplierCode:
      resolvedSupplier.supplier_code || undefined,

    supplierDbId:
      resolvedSupplier.id,

    description:
      merged.description ||
      resolvedSupplier.default_description ||
      undefined,

    category:
      merged.category ||
      resolvedSupplier.industry ||
      undefined,
  };
}

setDraftDetails(merged);

      let reply: string | null = null;

      if (!merged.supplier || !merged.description) {
          reply = buildIntroReply(userName);
      }

    window.setTimeout(() => {
      if (reply) {
        setMessages((prev) => [...prev, { id: prev.length + 1, role: "agent", text: reply as string }]);
        setIsTyping(false);
        if (followUp) setLinkedFollowUp(followUp);
        return;
      }

      setIsTyping(false);
      void runVerification(merged);
    }, 700);
  };

  if (showDetails && record) {
    return <PrDetailsPanel record={record} onRunAgent={runAgentEvaluation} />;
  }

  return (
    <section className="iac-engine__card">
      <div className="iac-engine__card-head">
        <h3>PR Creation</h3>
      </div>

      <div className="iac-engine__chat">
        <div className="iac-engine__chat-scroll" ref={scrollRef}>
          {messages.length === 0 ? (
            <>
              <div className="iac-engine__chat-intro">
                <span className="iac-engine__chat-badge" aria-hidden="true">
                  <Sparkles size={18} />
                </span>
                <h4 className="iac-engine__chat-title">Hi {userName}, Create New PR through Chat</h4>
                <p className="iac-engine__chat-subtitle">
                  Tell me what you need to purchase and I'll draft a new PR for review
                </p>
              </div>

              <div className="iac-engine__suggestions">
                {SUGGESTIONS.map((suggestion) => (
                  <button
                    key={suggestion.prompt}
                    type="button"
                    className="iac-engine__suggestion"
                    title={suggestion.prompt}
                    onClick={() => submit(suggestion.prompt, suggestion.followUp)}
                  >
                    {suggestion.prompt}
                  </button>
                ))}
              </div>
            </>
          ) : (
            messages.map((message) => (
              <div key={message.id} className={`iac-engine__row iac-engine__row--${message.role}`}>
                {message.role === "agent" && (
                  <span className="iac-engine__avatar iac-engine__avatar--ai" aria-hidden="true">
                    AI
                  </span>
                )}
                <div className={`iac-engine__message iac-engine__message--${message.role}`}>{message.text}</div>
                {message.role === "user" && (
                  <span className="iac-engine__avatar iac-engine__avatar--user" aria-hidden="true">
                    {userInitials}
                  </span>
                )}
              </div>
            ))
          )}

          {linkedFollowUp && stage === "collecting" && !isTyping && (
            <>
              <div className="iac-engine__suggestions iac-engine__suggestions--inline flex flex-col ">
                <span className="flex flex-col gap-2">
                  <p className="iac-engine__suggestions-label">Suggested Responses</p>
                  <button
                    type="button"
                    className="iac-engine__suggestion"
                    title={linkedFollowUp}
                    onClick={() => submit(linkedFollowUp)}
                  >
                    {linkedFollowUp}
                  </button>
                  <button
                    type="button"
                    className="iac-engine__suggestion"
                    onClick={reset}
                  >
                    Cancel PR creation flow
                  </button>
                </span>
                
              </div>
            </>
          )}

          {isTyping && (
            <div className="iac-engine__row iac-engine__row--agent">
              <span className="iac-engine__avatar iac-engine__avatar--ai" aria-hidden="true">
                AI
              </span>
              <div className="iac-engine__typing" role="status" aria-label="Assistant is typing">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}

          {stage === "verifying" && (
            <div className="iac-engine__row iac-engine__row--agent">
              <span className="iac-engine__avatar iac-engine__avatar--ai" aria-hidden="true">
                AI
              </span>
              <div className="iac-engine__verify">
                <p className="iac-engine__verify-head">
                  <RefreshCw size={14} className="iac-engine__spin" />
                  Verifying IRC eligibility now... Let me check compliance criteria and validate details.
                </p>
                <ul className="iac-engine__verify-steps">
                  {steps.map((step) => (
                    <li key={step} className="is-done">
                      <Check size={13} /> {step}
                    </li>
                  ))}
                  <li className="is-pending">
                    <span className="iac-engine__dot" /> Verifying IRC eligibility...
                  </li>
                </ul>
              </div>
            </div>
          )}

          {stage === "verified" && record && (
            <div className="iac-engine__row iac-engine__row--agent">
              <span className="iac-engine__avatar iac-engine__avatar--ai" aria-hidden="true">
                AI
              </span>
              <div className="iac-engine__result">
                <p className="iac-engine__result-head">
                  <CheckCircle2 size={16} />
                  {record.isEligible
                    ? "IRC eligibility has been verified successfully!"
                    : "IRC eligibility check complete — this request needs review."}
                </p>

                <div className="iac-engine__result-card">
                  <div className="iac-engine__result-top">
                    <div>
                      <span className="iac-engine__result-label">Eligibility ID</span>
                      <strong className="iac-engine__result-id">{record.eligibilityId}</strong>
                    </div>
                    <span className={`iac-engine__chip${record.isEligible ? " iac-engine__chip--good" : " iac-engine__chip--bad"}`}>
                      {record.isEligible ? "Verified" : "Review"}
                    </span>
                  </div>

                  <dl className="iac-engine__result-rows">
                    <div><dt>Supplier:</dt><dd>{record.supplier}</dd></div>
                    <div><dt>Category:</dt><dd>{record.category}</dd></div>
                    <div>
                      <dt>Amount:</dt>
                      <dd className="iac-engine__result-amount">
                        {record.amount == null
                          ? "—"
                          : `${record.currency === "USD" ? "$" : "₹"}${record.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
                      </dd>
                    </div>
                    <div><dt>IRC Status:</dt><dd>{record.isEligible ? "Eligible" : "Not eligible"}</dd></div>
                  </dl>

                  <div className="iac-engine__result-actions">
                    <button type="button" className="iac-engine__btn iac-engine__btn--primary" onClick={() => void openDetails()} disabled={isUploadingBackendData}>
                      {isUploadingBackendData ? <RefreshCw size={15} className="iac-engine__spin" /> : null}
                      {isUploadingBackendData ? "Preparing Details..." : "View Details"}
                    </button>
                    <button type="button" className="iac-engine__btn iac-engine__btn--outline" onClick={reset}>
                      Check Another
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <form
          className="iac-engine__composer"
          onSubmit={(event) => {
            event.preventDefault();
            submit(draft);
          }}
        >
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Describe what you need to purchase..."
            aria-label="Describe what you need to purchase"
            disabled={stage !== "collecting"}
          />
          <button
            type="submit"
            className="iac-engine__send"
            disabled={!draft.trim() || isTyping || stage !== "collecting"}
            aria-label="Send request"
          >
            <Send size={22} />
          </button>
        </form>
      </div>
    </section>
  );
}
